/**
 * Shared constants and types for the audio-transcription pipeline.
 *
 * This module is the canonical home for the values and shapes that cross
 * module boundaries inside the renderer-side audio-transcription service.
 * Everything here is intentionally framework-free (no DOM globals beyond
 * standard types, no Sentry, no Electron) so individual sub-modules can be
 * unit-tested in isolation.
 */

/** Numeric source identifier for the microphone channel. The legacy
 *  `/transcribe-ws` backend uses these integers in its message payloads;
 *  GlobalState consumers downstream still key off them, so we preserve
 *  the int representation throughout. */
export const SOURCE_MICROPHONE = 0
/** Numeric source identifier for the system loopback channel. */
export const SOURCE_LOOPBACK = 1
/** Numeric source identifier meaning "either / both" for service-level APIs
 *  like forceSegment(). Not sent over the wire. */
export const SOURCE_BOTH = 2

/** Sample rate Deepgram is configured for server-side. Wrapped system
 *  loopback frames arrive at this rate from Web Audio; raw mic frames
 *  are resampled to this rate by AudioPcmPipeline before PCM is sent. */
export const TARGET_SAMPLE_RATE = 16000

/** Bytes per second of PCM at the target rate, mono, 16-bit. Used to convert
 *  buffered-byte counts to wall-clock seconds for UI / diagnostics. */
export const TARGET_BYTES_PER_SECOND = TARGET_SAMPLE_RATE * 2

/** Cadence for publishing the broadcast audio-level meter snapshots. Renderer
 *  consumers subscribe directly; main reads systemLevel via a pull snapshot for
 *  the meeting-end veto. */
export const ENERGY_REPORT_INTERVAL_MS = 100

/** Max PCM bytes to buffer per channel during WS disconnect (~2 min of
 *  16kHz mono 16-bit). Anything beyond this drops on the floor and is
 *  surfaced to the user via insertGapMarker(). */
export const MAX_BUFFER_BYTES = 3_840_000

/** Max time to wait for a forced-segment final transcript before giving up.
 *  Bounds the dictation stop path on slow networks; the await still resolves
 *  promptly when the server returns the final segment. */
export const FORCE_SEGMENT_TIMEOUT_MS = 5000

/** Fallback flush timeout if the server never sends "Transcription session started".
 *  In practice this status arrives within ~1-2s of WS open, so 10s is generous
 *  for normal operation but bounds worst-case audio loss if the server regresses. */
export const SERVER_READY_FALLBACK_MS = 10000

/** Service-level lifecycle state. Mirrors what's broadcast on
 *  `audio-transcription-service.status`. */
export type ServiceStatus = 'idle' | 'starting' | 'running' | 'stopping' | 'reconnecting' | 'paused'

/** Channel identifier label used in logs / Sentry tags. Mirrors the
 *  payload field name used by the audio-sessions DO for `dgSpeakerIndex`
 *  (which is `mic:N` / `sys:N`). NOTE: this is NOT the same as the
 *  WebSocket URL's `channel` query param — that one is `'mic' | 'system'`
 *  on the server side. Use `wireChannelName()` when building the URL. */
export type ChannelLabel = 'mic' | 'sys'

/** WebSocket URL `channel` query-param values, as expected by
 *  `AudioSessionObject` server-side (`'mic' | 'system'`). Kept separate
 *  from `ChannelLabel` because the internal `'sys'` spelling is not a
 *  valid wire value for the audio-sessions DO. */
export type WireChannelName = 'mic' | 'system'

/** Map an internal `ChannelLabel` to the wire value the audio-sessions
 *  DO expects on the URL. */
export function wireChannelName(label: ChannelLabel): WireChannelName {
  return label === 'mic' ? 'mic' : 'system'
}

/**
 * Stable, opaque key identifying which input device a mic channel is
 * capturing. The audio-sessions DO compares it across reconnects to tell a
 * same-device blip (rebind onto the live Deepgram session — speaker profiles
 * survive) from an input-device switch (re-dial, because the new mic's
 * acoustics would otherwise read as a new voice mid-stream).
 *
 * FNV-1a over the deviceId, hex. We deliberately do NOT send the raw
 * `deviceId` or the device label: the server only ever needs "same or
 * different", never which microphone it is. `undefined` (the OS default
 * device) maps to a fixed key so default→default stays a rebind.
 */
export function micDeviceKeyFor(deviceId: string | undefined): string {
  const input = deviceId ?? 'default'
  let hash = 0x811c9dc5
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193) >>> 0
  }
  return hash.toString(16)
}

/** Public channel values published on `audio-transcription-service.captureError`.
 *  This GlobalState payload uses the full system channel name consumed by main
 *  process UI surfaces, not the internal abbreviated `ChannelLabel` spelling. */
export type CaptureErrorChannel = 'mic' | 'system'

export interface TextSegment {
  text: string
  startTime: number
  endTime: number
  source: number
  speaker?: string
  isHost?: boolean
  /** The DO's mic epoch for a mic segment; see `TranscriptionMessage.micEpoch`. */
  micEpoch?: number
}

/**
 * Parsed shape of an audio-WS message from the backend.
 *
 * Two backends produce these:
 *   - legacy `/transcribe-ws` (numeric source field, no dgSpeakerIndex)
 *   - audio-sessions DO `/api/v1/audio-sessions/:id/ws` (string source,
 *     dgSpeakerIndex prefixed with `mic:` / `sys:`)
 * Both shapes coexist so all fields are optional.
 */
export interface TranscriptionMessage {
  type: string
  text?: string
  id?: string
  /** Numeric source for legacy /transcribe-ws messages (0 = mic, 1 = system).
   *  The audio-sessions DO instead identifies sources by the string field
   *  below, so both shapes coexist. */
  source?: number | 'mic' | 'system'
  speaker?: string
  is_host?: boolean
  isHost?: boolean
  timestamp?: number
  offset?: number
  duration?: number
  error?: string
  message?: string
  status?: string
  reason?: string
  /** Stable, source-prefixed diarization key (e.g. `"mic:0"`, `"sys:5"`) set
   *  by the audio-sessions DO. The DO already namespaces speaker indices
   *  across its own Deepgram reconnects, so when this is present the client
   *  must NOT layer its own offset on top — see AudioChannel.handleMessage().
   *  The legacy /transcribe-ws backend doesn't send it, in which case the
   *  client's per-channel offset is the only thing keeping speakers stable
   *  across client-side reconnects. */
  dgSpeakerIndex?: string
  /** Mic finals from the audio-sessions DO: which Deepgram stream the mic
   *  speaker number belongs to. The mic keeps its numbering across streams,
   *  so `mic:1` can be a different voice in each epoch. */
  micEpoch?: number
}

export interface CaptureErrorPayload {
  channel: CaptureErrorChannel
  error: string
}
